import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast, useConfirm, usePrompt } from "../context/UIContext";
import { getAllUsers } from "../services/UserService";
import {
    createEmployeeProfileForUser,
    deleteProfilePhoto,
    getEmployeeProfileForUser,
    getProfilePhotoBlob,
    updateEmployeeProfileForUser,
    uploadProfilePhoto,
} from "../services/EmployeeProfile";
import {
    deleteCvAttachment,
    getCvAttachmentBlob,
    getCvAttachmentsForUser,
    updateCvAttachment,
    uploadCvAttachment,
} from "../services/CvAttachmentService";
import {
    createEmployeeProjectForUser,
    deleteEmployeeProject,
    getEmployeeProjectsForUser,
    updateEmployeeProject,
} from "../services/EmployeeProjects";
import { getCurrentUserRole } from "../utils/authHelper";
import { formatDate } from "../utils/dateConverter";
import { deleteCvFile, generateCvPdf, listCvFilesForUser } from "../services/CurriculumVitaeService";
import {
    createProfileVariantForUser,
    deleteProfileVariant,
    getProfileVariantsForUser,
    updateProfileVariant,
} from "../services/ProfileVariantService";
import { extractApiError } from '../utils/apiError';
import { closePendingTab, openPendingTab, revealBlobInTab } from "../utils/pdfTab";
import {
    SECTION_META,
    SECTION_ORDER,
    arrayToMultiline,
    countIncomplete,
    emptyItem,
    entrySummary,
    extraSectionKeys,
    hasContent,
    isIncomplete,
    monthInputValue,
    moveEntry,
    normaliseSections,
    INDUSTRY_SUGGESTIONS,
    parseBulletLines,
    parseSkillsText,
    profileSnapshot,
    skillsToText,
    stripUids,
    withUid,
    VARIANT_ALL,
    VARIANT_PICK,
    emptyVariantForm,
    formToVariant,
    variantFileMatch,
    variantFormToPayload,
    variantScope,
    variantScopeLabel,
    variantSnapshot,
    variantToForm,
} from "../utils/cvProfile";

import "../styles/EmployeeProfiles.css";

// A <label> wrapping its own control: implicit association, no id bookkeeping across an
// arbitrary number of entries. `span` sizes the field by what it holds.

const Field = ({ label, hint, span = "md", children }) => (
    <label className={`cv-field cv-field--${span}`}>
        <span className="cv-field-label">
            {label}
            {hint && <span className="cv-field-hint">{hint}</span>}
        </span>
        {children}
    </label>
);

// The syntax of a bullet textarea, next to the field rather than in a manual: both
// conventions are invisible otherwise.
const BULLET_HINT = "ein Eintrag pro Zeile · eingerückt = Unterpunkt · Leerzeile = Abstand";

const CheckField = ({ label, span = "sm", checked, onChange }) => (
    <label className={`cv-field cv-field--${span} cv-field--check`}>
        <span className="cv-field-label" aria-hidden="true">&nbsp;</span>
        <span className="cv-check">
            <input type="checkbox" checked={checked} onChange={onChange} />
            {label}
        </span>
    </label>
);

export default function EmployeeProfiles() {
    const toast = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const [role, setRole] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [activeTab, setActiveTab] = useState("profile");

    // `variantForm` is the variant being edited (null = none open); `cvVariantId` is what the
    // generate tab renders through ("" = whole profile).
    const [variants, setVariants] = useState([]);
    const [variantsLoading, setVariantsLoading] = useState(false);
    const [variantForm, setVariantForm] = useState(null);
    const [variantBaseline, setVariantBaseline] = useState("");
    const [variantSaving, setVariantSaving] = useState(false);
    const [cvVariantId, setCvVariantId] = useState("");

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [profileExists, setProfileExists] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");

    const [profile, setProfile] = useState({ title: "", summary: "", birthDate: "" });
    const [skills, setSkills] = useState([]);
    const [newSkill, setNewSkill] = useState("");
    const [sections, setSections] = useState(() => normaliseSections({}));
    const [openEntries, setOpenEntries] = useState({});
    const [baseline, setBaseline] = useState("");

    const [projects, setProjects] = useState([]);
    const [newProject, setNewProject] = useState({ title: "", industry: "", startDate: "", endDate: "", skillsText: "", description: "", sortOrder: 0 });
    const [showNewProject, setShowNewProject] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editProject, setEditProject] = useState({ title: "", industry: "", startDate: "", endDate: "", skillsText: "", description: "", sortOrder: 0 });

    const [hasPhoto, setHasPhoto] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoBusy, setPhotoBusy] = useState(false);

    // The attachments belong to the profile; which of them a PDF carries is decided per
    // generation, so the ticked ids live next to the other generate-tab fields.
    const [attachments, setAttachments] = useState([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [attachmentBusy, setAttachmentBusy] = useState(null);
    const [attachmentTitle, setAttachmentTitle] = useState("");
    const [cvAttachmentIds, setCvAttachmentIds] = useState([]);
    const [cvIncludePhoto, setCvIncludePhoto] = useState(false);

    const [cvFiles, setCvFiles] = useState([]);
    const [cvFilesLoading, setCvFilesLoading] = useState(false);
    const [cvFileDeleting, setCvFileDeleting] = useState(null);
    const [cvGenerating, setCvGenerating] = useState(false);
    const [cvTitle, setCvTitle] = useState("");
    const [cvComment, setCvComment] = useState("");

    useEffect(() => {
        const r = getCurrentUserRole();
        setRole(r);
        if (r === "admin") loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const u = await getAllUsers();
            setUsers(u);
            if (u.length > 0) setSelectedUserId(u[0].id);
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Laden der Benutzer"));
        }
    };

    // The uids on screen, handed to normaliseSections so a reload after saving keeps them —
    // and with them the expanded state, which is keyed by uid.
    const sectionsRef = useRef(sections);
    useEffect(() => { sectionsRef.current = sections; }, [sections]);

    // The loaders are memoised so the reload effect can declare them honestly;
    // they only touch state setters, which are stable.
    const applyProfile = useCallback((p) => {
        const nextProfile = { title: p.title ?? "", summary: p.summary ?? "", birthDate: p.birthDate ? p.birthDate.slice(0, 10) : "" };
        const nextSkills = Array.isArray(p.skills) ? p.skills : [];
        const nextSections = normaliseSections(p.sections, sectionsRef.current);
        setProfile(nextProfile);
        setSkills(nextSkills);
        setSections(nextSections);
        setBaseline(profileSnapshot(nextProfile, nextSkills, nextSections));
    }, []);

    // The photo is held as an object URL, which has to be revoked — a new one is minted on
    // every load and on every upload.
    const showPhoto = useCallback(async (userId) => {
        try {
            const blob = await getProfilePhotoBlob(userId);
            setPhotoUrl((old) => {
                if (old) window.URL.revokeObjectURL(old);
                return window.URL.createObjectURL(blob);
            });
        } catch {
            setPhotoUrl((old) => {
                if (old) window.URL.revokeObjectURL(old);
                return null;
            });
        }
    }, []);

    const loadProfile = useCallback(async (userId) => {
        try {
            const p = await getEmployeeProfileForUser(userId);
            setProfileExists(true);
            applyProfile(p);
            setHasPhoto(!!p.hasPhoto);
            if (p.hasPhoto) await showPhoto(userId);
        } catch {
            setProfileExists(false);
            applyProfile({});
            setHasPhoto(false);
        }
    }, [applyProfile, showPhoto]);

    const loadAttachments = useCallback(async (userId) => {
        setAttachmentsLoading(true);
        try {
            const data = await getCvAttachmentsForUser(userId);
            setAttachments(Array.isArray(data) ? data : []);
        } catch { setAttachments([]); }
        finally { setAttachmentsLoading(false); }
    }, []);

    const loadProjects = useCallback(async (userId) => {
        try { setProjects(await getEmployeeProjectsForUser(userId)); }
        catch { setProjects([]); }
    }, []);

    const loadCvFiles = useCallback(async (userId) => {
        setCvFilesLoading(true);
        try {
            const data = await listCvFilesForUser(userId);
            setCvFiles(Array.isArray(data?.files) ? data.files : []);
        } catch { setCvFiles([]); }
        finally { setCvFilesLoading(false); }
    }, []);

    const loadVariants = useCallback(async (userId) => {
        setVariantsLoading(true);
        try {
            const data = await getProfileVariantsForUser(userId);
            setVariants([...data].sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name, "de")));
        } catch { setVariants([]); }
        finally { setVariantsLoading(false); }
    }, []);

    useEffect(() => {
        if (!selectedUserId) return;
        setProfileLoaded(false);
        setOpenEntries({});
        setEditId(null);
        setShowNewProject(false);
        setVariantForm(null);
        setCvVariantId("");
        // Another employee's selection is not this employee's: a ticked attachment id
        // would otherwise travel to a CV it does not belong to (the API answers 404).
        setCvAttachmentIds([]);
        setCvIncludePhoto(false);
        setAttachmentTitle("");
        setPhotoUrl((old) => {
            if (old) window.URL.revokeObjectURL(old);
            return null;
        });
        Promise.all([
            loadProfile(selectedUserId),
            loadProjects(selectedUserId),
            loadCvFiles(selectedUserId),
            loadVariants(selectedUserId),
            loadAttachments(selectedUserId),
        ]).finally(() => setProfileLoaded(true));
    }, [selectedUserId, loadProfile, loadProjects, loadCvFiles, loadVariants, loadAttachments]);

    const snapshot = useMemo(() => profileSnapshot(profile, skills, sections), [profile, skills, sections]);
    const dirty = profileLoaded && snapshot !== baseline;

    const sectionStats = useMemo(() => SECTION_ORDER.map((type) => {
        const items = sections[type] || [];
        return { type, count: items.length, incomplete: countIncomplete(type, items) };
    }), [sections]);

    const entryTotal = sectionStats.reduce((sum, s) => sum + s.count, 0);
    const incompleteTotal = sectionStats.reduce((sum, s) => sum + s.incomplete, 0);
    const carriedKeys = extraSectionKeys(sections);

    const handleSaveProfile = async () => {
        if (!selectedUserId) return;
        setSaving(true);
        setSaveMsg("");
        const dto = { title: profile.title || null, summary: profile.summary || null, birth_date: profile.birthDate || null, skills, sections: stripUids(sections) };
        try {
            if (profileExists) {
                await updateEmployeeProfileForUser(selectedUserId, dto);
            } else {
                await createEmployeeProfileForUser(selectedUserId, dto);
                setProfileExists(true);
            }
            setSaveMsg("Gespeichert");
            setTimeout(() => setSaveMsg(""), 2500);
            await loadProfile(selectedUserId);
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Speichern"));
        } finally {
            setSaving(false);
        }
    };

    const addSkill = (raw) => {
        const incoming = parseSkillsText(raw);
        if (incoming.length === 0) return;
        setSkills((prev) => {
            const next = [...prev];
            incoming.forEach((s) => {
                if (!next.some((x) => x.toLowerCase() === s.toLowerCase())) next.push(s);
            });
            return next;
        });
        setNewSkill("");
    };

    const removeSkill = (s) => setSkills((prev) => prev.filter((x) => x !== s));

    const onSkillKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addSkill(newSkill); }
        if (e.key === "Backspace" && !newSkill && skills.length > 0) removeSkill(skills[skills.length - 1]);
    };

    const jumpToSection = (id) => {
        const el = document.getElementById(`cv-sec-${id}`);
        if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const toggleEntry = (uid) =>
        setOpenEntries((prev) => ({ ...prev, [uid]: !prev[uid] }));

    const addSectionItem = (type) => {
        const item = withUid(emptyItem(type));
        setSections((prev) => ({ ...prev, [type]: [...(prev[type] || []), item] }));
        setOpenEntries((prev) => ({ ...prev, [item._uid]: true }));
    };

    const removeSectionItem = async (type, idx) => {
        const item = (sections[type] || [])[idx];
        if (hasContent(item)) {
            const { title } = entrySummary(type, item);
            const what = title ? `„${title}“` : "diesen Eintrag";
            if (!await confirm(`${what} aus ${SECTION_META[type].label} entfernen?`, { confirmLabel: "Entfernen", danger: true })) return;
        }
        setSections((prev) => { const a = [...(prev[type] || [])]; a.splice(idx, 1); return { ...prev, [type]: a }; });
    };

    const moveSectionItem = (type, idx, direction) =>
        setSections((prev) => ({ ...prev, [type]: moveEntry(prev[type], idx, direction) }));

    const updateSectionField = (type, idx, field, value) =>
        setSections((prev) => {
            const a = [...(prev[type] || [])];
            a[idx] = { ...a[idx], [field]: value };
            return { ...prev, [type]: a };
        });

    const updateSectionArrayText = (type, idx, field, text) =>
        updateSectionField(type, idx, field, parseBulletLines(text));

    const handleCreateProject = async () => {
        if (!selectedUserId || !newProject.title) { toast("Bitte Titel eingeben"); return; }
        try {
            await createEmployeeProjectForUser(selectedUserId, {
                title: newProject.title, industry: newProject.industry || null,
                start_date: newProject.startDate || null,
                end_date: newProject.endDate || null, skills: parseSkillsText(newProject.skillsText),
                description: newProject.description || null, sort_order: Number(newProject.sortOrder || 0),
            });
            setNewProject({ title: "", industry: "", startDate: "", endDate: "", skillsText: "", description: "", sortOrder: 0 });
            setShowNewProject(false);
            await loadProjects(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Fehler beim Anlegen")); }
    };

    const startEdit = (p) => {
        setEditId(p.id);
        setEditProject({ title: p.title || "", industry: p.industry || "", startDate: p.startDate ? p.startDate.slice(0, 10) : "", endDate: p.endDate ? p.endDate.slice(0, 10) : "", skillsText: skillsToText(p.skills), description: p.description || "", sortOrder: p.sortOrder ?? 0 });
    };

    const cancelEdit = () => { setEditId(null); };

    const toggleProjectEdit = (p) => (editId === p.id ? cancelEdit() : startEdit(p));

    const saveEdit = async () => {
        if (!editId || !editProject.title) { toast("Bitte Titel eingeben"); return; }
        try {
            await updateEmployeeProject(editId, { title: editProject.title, industry: editProject.industry || null, start_date: editProject.startDate || null, end_date: editProject.endDate || null, skills: parseSkillsText(editProject.skillsText), description: editProject.description || null, sort_order: Number(editProject.sortOrder || 0) });
            cancelEdit();
            await loadProjects(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Fehler beim Speichern")); }
    };

    const handleDeleteProject = async (id) => {
        if (!await confirm("Projekt wirklich löschen?", { confirmLabel: 'Löschen', danger: true })) return;
        try { await deleteEmployeeProject(id); await loadProjects(selectedUserId); }
        catch (err) { toast(extractApiError(err, "Fehler beim Löschen")); }
    };

    // What the profile currently offers a variant. `variantToForm` needs it to drop entries the
    // profile no longer has instead of leaving a ghost ticked.
    const variantAvailable = useMemo(
        () => ({ skills, projectIds: projects.map((p) => String(p.id)) }),
        [skills, projects],
    );

    const openVariant = (variant) => {
        const form = variant ? variantToForm(variant, variantAvailable) : emptyVariantForm();
        setVariantForm(form);
        setVariantBaseline(variantSnapshot(form));
    };

    const closeVariant = () => setVariantForm(null);

    const setVariantField = (field, value) =>
        setVariantForm((prev) => (prev ? { ...prev, [field]: value } : prev));

    /** Toggle one picked skill / project id, keeping the variant's own order. */
    const toggleVariantPick = (field, value) =>
        setVariantForm((prev) => {
            if (!prev) return prev;
            const list = prev[field] || [];
            return {
                ...prev,
                [field]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value],
            };
        });

    const handleSaveVariant = async () => {
        if (!variantForm || !selectedUserId) return;
        const payload = variantFormToPayload(variantForm);
        if (!payload.name) { toast("Bitte einen Namen für die Variante angeben."); return; }
        setVariantSaving(true);
        try {
            if (variantForm.id) await updateProfileVariant(variantForm.id, payload);
            else await createProfileVariantForUser(selectedUserId, payload);
            await loadVariants(selectedUserId);
            setVariantForm(null);
            toast(variantForm.id ? "Variante gespeichert." : "Variante angelegt.", "success");
        } catch (err) { toast(extractApiError(err, "Variante konnte nicht gespeichert werden")); }
        finally { setVariantSaving(false); }
    };

    const handleDeleteVariant = async (variant) => {
        if (!await confirm(`Variante „${variant.name}" wirklich löschen?`, { confirmLabel: "Löschen", danger: true })) return;
        try {
            await deleteProfileVariant(variant.id);
            // The generate tab may have been pointing at it.
            if (String(cvVariantId) === String(variant.id)) setCvVariantId("");
            if (variantForm?.id === variant.id) setVariantForm(null);
            await loadVariants(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Variante konnte nicht gelöscht werden")); }
    };

    const variantDirty = variantForm ? variantSnapshot(variantForm) !== variantBaseline : false;

    // Scope of the variant currently open in the editor, and of the one the generate
    // tab will render through — the same sentence in both places.
    const editorScope = variantForm ? variantScope(formToVariant(variantForm), skills, projects) : null;
    const selectedVariant = variants.find((v) => String(v.id) === String(cvVariantId)) || null;
    const generateScope = selectedVariant ? variantScope(selectedVariant, skills, projects) : null;

    const handleGenerateCv = async () => {
        if (!selectedUserId) return;
        setCvGenerating(true);
        try {
            const { blob, filename } = await generateCvPdf(selectedUserId, {
                title: (cvTitle || profile.title || "CV").trim(),
                comment: cvComment || null,
                variantId: cvVariantId || null,
                // In the order the list shows them: the backend sorts them back into the
                // stored order anyway, so a CV cannot depend on the order of the clicks.
                attachmentIds: attachments.filter((a) => cvAttachmentIds.includes(a.id)).map((a) => a.id),
                includePhoto: cvIncludePhoto && hasPhoto,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            await loadCvFiles(selectedUserId);
        } catch (err) { toast(extractApiError(err, "CV Generierung fehlgeschlagen")); }
        finally { setCvGenerating(false); }
    };

    const handleDeleteCvFile = async (file) => {
        const name = file.fileName || file.key;
        if (!await confirm(`„${name}" wirklich löschen?`, { confirmLabel: "Löschen", danger: true })) return;
        setCvFileDeleting(file.key);
        try {
            await deleteCvFile(selectedUserId, name);
            await loadCvFiles(selectedUserId);
        } catch (err) { toast(extractApiError(err, "PDF konnte nicht gelöscht werden")); }
        // Only when it is still this row's — two deletes in flight would otherwise
        // re-enable the one still running.
        finally { setCvFileDeleting((cur) => (cur === file.key ? null : cur)); }
    };


    const handleUploadPhoto = async (file) => {
        if (!file || !selectedUserId) return;
        setPhotoBusy(true);
        try {
            await uploadProfilePhoto(selectedUserId, file);
            setHasPhoto(true);
            await showPhoto(selectedUserId);
            toast("Foto gespeichert.", "success");
        } catch (err) { toast(extractApiError(err, "Foto konnte nicht gespeichert werden")); }
        finally { setPhotoBusy(false); }
    };

    const handleDeletePhoto = async () => {
        if (!await confirm("Foto wirklich entfernen?", { confirmLabel: "Entfernen", danger: true })) return;
        setPhotoBusy(true);
        try {
            await deleteProfilePhoto(selectedUserId);
            setHasPhoto(false);
            setCvIncludePhoto(false);
            setPhotoUrl((old) => {
                if (old) window.URL.revokeObjectURL(old);
                return null;
            });
        } catch (err) { toast(extractApiError(err, "Foto konnte nicht entfernt werden")); }
        finally { setPhotoBusy(false); }
    };

    const handleUploadAttachment = async (file) => {
        if (!file || !selectedUserId) return;
        setAttachmentBusy("upload");
        try {
            await uploadCvAttachment(selectedUserId, file, attachmentTitle.trim());
            setAttachmentTitle("");
            await loadAttachments(selectedUserId);
            toast("Anhang hinzugefügt.", "success");
        } catch (err) { toast(extractApiError(err, "Anhang konnte nicht hochgeladen werden")); }
        finally { setAttachmentBusy((cur) => (cur === "upload" ? null : cur)); }
    };

    const handleRenameAttachment = async (attachment) => {
        const title = await prompt(`Titel von „${attachment.title}"`, {
            confirmLabel: "Speichern", defaultValue: attachment.title,
        });
        if (title === null) return;  // cancelled
        if (!title.trim()) { toast("Der Titel darf nicht leer sein."); return; }
        setAttachmentBusy(attachment.id);
        try {
            await updateCvAttachment(attachment.id, { title: title.trim() });
            await loadAttachments(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Anhang konnte nicht umbenannt werden")); }
        finally { setAttachmentBusy((cur) => (cur === attachment.id ? null : cur)); }
    };

    // Moving is a swap of two sort orders, so a list that was imported with duplicates
    // cannot get stuck: the two rows exchange the positions they occupy in the list.
    const handleMoveAttachment = async (index, delta) => {
        const target = attachments[index + delta];
        const current = attachments[index];
        if (!target || !current) return;
        setAttachmentBusy(current.id);
        try {
            await updateCvAttachment(current.id, { sort_order: index + delta });
            await updateCvAttachment(target.id, { sort_order: index });
            await loadAttachments(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Reihenfolge konnte nicht geändert werden")); }
        finally { setAttachmentBusy((cur) => (cur === current.id ? null : cur)); }
    };

    const handleDeleteAttachment = async (attachment) => {
        if (!await confirm(`„${attachment.title}" wirklich löschen?`, { confirmLabel: "Löschen", danger: true })) return;
        setAttachmentBusy(attachment.id);
        try {
            await deleteCvAttachment(attachment.id);
            setCvAttachmentIds((ids) => ids.filter((id) => id !== attachment.id));
            await loadAttachments(selectedUserId);
        } catch (err) { toast(extractApiError(err, "Anhang konnte nicht gelöscht werden")); }
        finally { setAttachmentBusy((cur) => (cur === attachment.id ? null : cur)); }
    };

    // The tab is opened synchronously and navigated once the bytes arrive — `window.open`
    // only counts as user-initiated while the click handler is on the stack.
    const handlePreviewAttachment = async (attachment) => {
        const tab = openPendingTab(`${attachment.title} wird geladen …`);
        if (!tab) { toast("Das Popup wurde blockiert."); return; }
        try {
            revealBlobInTab(tab, await getCvAttachmentBlob(attachment.id));
        } catch (err) {
            closePendingTab(tab);
            toast(extractApiError(err, "Anhang konnte nicht geöffnet werden"));
        }
    };

    const toggleCvAttachment = (id) =>
        setCvAttachmentIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

    const renderSection = (type) => {
        const meta = SECTION_META[type];
        const items = sections[type] || [];
        const incomplete = countIncomplete(type, items);
        return (
            <section key={type} id={`cv-sec-${type}`} className="cv-section">
                <div className="cv-section-head">
                    <h3 className="cv-section-title">{meta.label}</h3>
                    <span className="cv-count">{items.length}</span>
                    {incomplete > 0 && <span className="cv-chip cv-chip--warn">{incomplete} unvollständig</span>}
                    <button type="button" className="button button-normal btn-sm cv-section-add" onClick={() => addSectionItem(type)}>
                        {meta.addLabel}
                    </button>
                </div>
                {meta.hint && <p className="cv-section-hint">{meta.hint}</p>}
                {items.length === 0 ? (
                    <p className="cv-empty">
                        {meta.empty}{" "}
                        <button type="button" className="cv-linkbtn" onClick={() => addSectionItem(type)}>
                            Jetzt anlegen
                        </button>
                    </p>
                ) : (
                    <ol className="cv-entries">
                        {items.map((item, idx) => renderEntry(type, item, idx, items.length))}
                    </ol>
                )}
            </section>
        );
    };

    const renderEntry = (type, item, idx, total) => {
        const open = !!openEntries[item._uid];
        const { title, detail, meta } = entrySummary(type, item);
        const name = title || `Eintrag ${idx + 1}`;
        return (
            <li key={item._uid} className={`cv-entry${open ? " cv-entry--open" : ""}`}>
                <div className="cv-entry-head">
                    <button type="button" className="cv-entry-toggle" aria-expanded={open} onClick={() => toggleEntry(item._uid)}>
                        <span className="cv-entry-chevron" aria-hidden="true">{open ? "▼" : "▶"}</span>
                        <span className={`cv-entry-title${title ? "" : " cv-entry-title--empty"}`}>
                            {title || "Ohne Bezeichnung"}
                        </span>
                        {detail && <span className="cv-entry-detail">{detail}</span>}
                        {isIncomplete(type, item) && <span className="cv-chip cv-chip--warn">unvollständig</span>}
                        {meta && <span className="cv-entry-meta">{meta}</span>}
                    </button>
                    <div className="cv-entry-actions">
                        <button type="button" className="cv-iconbtn" disabled={idx === 0}
                            onClick={() => moveSectionItem(type, idx, -1)} aria-label={`${name} nach oben verschieben`} title="Nach oben">↑</button>
                        <button type="button" className="cv-iconbtn" disabled={idx === total - 1}
                            onClick={() => moveSectionItem(type, idx, 1)} aria-label={`${name} nach unten verschieben`} title="Nach unten">↓</button>
                        <button type="button" className="cv-iconbtn cv-iconbtn--danger"
                            onClick={() => removeSectionItem(type, idx)} aria-label={`${name} entfernen`} title="Entfernen">✕</button>
                    </div>
                </div>
                {open && <div className="cv-entry-body">{renderItemFields(type, item, idx)}</div>}
            </li>
        );
    };

    const renderItemFields = (type, item, idx) => {
        const upd = (field, val) => updateSectionField(type, idx, field, val);
        const updArr = (field, text) => updateSectionArrayText(type, idx, field, text);

        switch (type) {
            case "experience":
                return (
                    <div className="cv-fields">
                        <Field label="Position" span="lg">
                            <input className="input" value={item.position || ""} onChange={(e) => upd("position", e.target.value)} placeholder="z.B. Senior Engineer" />
                        </Field>
                        <Field label="Unternehmen" span="lg">
                            <input className="input" value={item.company || ""} onChange={(e) => upd("company", e.target.value)} />
                        </Field>
                        <Field label="Standort" span="lg">
                            <input className="input" value={item.location || ""} onChange={(e) => upd("location", e.target.value)} />
                        </Field>
                        <Field label="Beginn" span="sm">
                            <input type="month" className="input" value={monthInputValue(item.start_date)} onChange={(e) => upd("start_date", e.target.value)} />
                        </Field>
                        <Field label="Ende" span="sm">
                            <input type="month" className="input" disabled={!!item.current} value={monthInputValue(item.end_date)} onChange={(e) => upd("end_date", e.target.value)} />
                        </Field>
                        <CheckField label="Läuft noch" checked={!!item.current}
                            onChange={(e) => { upd("current", e.target.checked); if (e.target.checked) upd("end_date", null); }} />
                        <Field label="Aufgaben" hint={BULLET_HINT} span="full">
                            <textarea className="input cv-textarea" value={arrayToMultiline(item.responsibilities)} onChange={(e) => updArr("responsibilities", e.target.value)} placeholder={"Entwicklung von …\n  Teilaufgabe (eingerückt)"} />
                        </Field>
                        <Field label="Erfolge" hint={BULLET_HINT} span="full">
                            <textarea className="input cv-textarea--sm" value={arrayToMultiline(item.achievements)} onChange={(e) => updArr("achievements", e.target.value)} placeholder="Ladezeit um 40 % reduziert …" />
                        </Field>
                    </div>
                );
            case "education":
                return (
                    <div className="cv-fields">
                        <Field label="Abschluss" span="lg">
                            <input className="input" value={item.degree || ""} onChange={(e) => upd("degree", e.target.value)} placeholder="z.B. M.Sc." />
                        </Field>
                        <Field label="Fachrichtung" span="lg">
                            <input className="input" value={item.field || ""} onChange={(e) => upd("field", e.target.value)} />
                        </Field>
                        <Field label="Institution" span="lg">
                            <input className="input" value={item.institution || ""} onChange={(e) => upd("institution", e.target.value)} />
                        </Field>
                        <Field label="Standort" span="lg">
                            <input className="input" value={item.location || ""} onChange={(e) => upd("location", e.target.value)} />
                        </Field>
                        <Field label="Beginn" span="sm">
                            <input type="month" className="input" value={monthInputValue(item.start_date)} onChange={(e) => upd("start_date", e.target.value)} />
                        </Field>
                        <Field label="Ende" span="sm">
                            <input type="month" className="input" disabled={!!item.current} value={monthInputValue(item.end_date)} onChange={(e) => upd("end_date", e.target.value)} />
                        </Field>
                        <Field label="Note" span="sm">
                            <input className="input" value={item.grade || ""} onChange={(e) => upd("grade", e.target.value)} placeholder="1,5" />
                        </Field>
                        <CheckField label="Läuft noch" checked={!!item.current}
                            onChange={(e) => { upd("current", e.target.checked); if (e.target.checked) upd("end_date", null); }} />
                        <Field label="Abschlussarbeit" span="full">
                            <textarea className="input cv-textarea--sm" value={item.thesis || ""} onChange={(e) => upd("thesis", e.target.value)} />
                        </Field>
                    </div>
                );
            case "certifications":
                return (
                    <div className="cv-fields">
                        <Field label="Name" span="xl">
                            <input className="input" value={item.name || ""} onChange={(e) => upd("name", e.target.value)} placeholder="z.B. AWS Solutions Architect" />
                        </Field>
                        <Field label="Aussteller" span="lg">
                            <input className="input" value={item.issuer || ""} onChange={(e) => upd("issuer", e.target.value)} />
                        </Field>
                        <Field label="Datum" span="sm">
                            <input type="month" className="input" value={monthInputValue(item.date)} onChange={(e) => upd("date", e.target.value)} />
                        </Field>
                        <Field label="Credential ID" span="lg">
                            <input className="input" value={item.credential_id || ""} onChange={(e) => upd("credential_id", e.target.value)} />
                        </Field>
                    </div>
                );
            case "languages":
                return (
                    <div className="cv-fields">
                        <Field label="Sprache" span="lg">
                            <input className="input" value={item.language || ""} onChange={(e) => upd("language", e.target.value)} placeholder="Deutsch" />
                        </Field>
                        <Field label="Niveau" span="lg">
                            <input className="input" value={item.proficiency || ""} onChange={(e) => upd("proficiency", e.target.value)} placeholder="Muttersprache / C1 / B2 …" />
                        </Field>
                    </div>
                );
            case "awards":
                return (
                    <div className="cv-fields">
                        <Field label="Titel" span="xl">
                            <input className="input" value={item.title || ""} onChange={(e) => upd("title", e.target.value)} />
                        </Field>
                        <Field label="Aussteller" span="lg">
                            <input className="input" value={item.issuer || ""} onChange={(e) => upd("issuer", e.target.value)} />
                        </Field>
                        <Field label="Datum" span="sm">
                            <input type="month" className="input" value={monthInputValue(item.date)} onChange={(e) => upd("date", e.target.value)} />
                        </Field>
                        <Field label="Beschreibung" span="full">
                            <textarea className="input cv-textarea--sm" value={item.description || ""} onChange={(e) => upd("description", e.target.value)} />
                        </Field>
                    </div>
                );
            case "publications":
                return (
                    <div className="cv-fields">
                        <Field label="Titel" span="xl">
                            <input className="input" value={item.title || ""} onChange={(e) => upd("title", e.target.value)} />
                        </Field>
                        <Field label="Verlag / Konferenz" span="lg">
                            <input className="input" value={item.publisher || ""} onChange={(e) => upd("publisher", e.target.value)} />
                        </Field>
                        <Field label="Datum" span="sm">
                            <input type="month" className="input" value={monthInputValue(item.date)} onChange={(e) => upd("date", e.target.value)} />
                        </Field>
                        <Field label="Co-Autoren" hint="mit Komma trennen" span="full">
                            <input className="input" value={item.co_authors || ""} onChange={(e) => upd("co_authors", e.target.value)} />
                        </Field>
                    </div>
                );
            default:
                return null;
        }
    };

    // Project rows have their own API calls and are saved per row.

    const renderProjectFields = (state, setState) => (
        <div className="cv-fields">
            <Field label="Titel" span="xl">
                <input className="input" value={state.title} onChange={(e) => setState((x) => ({ ...x, title: e.target.value }))} placeholder="z.B. ERP-Einführung" />
            </Field>
            <Field label="Branche" hint="frei wählbar" span="md">
                {/* A datalist, not a select: the list is a shortcut, and a branch nobody
                    foresaw must not need a migration. */}
                <input className="input" list="cv-industries" value={state.industry}
                       onChange={(e) => setState((x) => ({ ...x, industry: e.target.value }))}
                       placeholder="z.B. Automotive" />
            </Field>
            <Field label="Beginn" span="sm">
                <input type="date" className="input" value={state.startDate} onChange={(e) => setState((x) => ({ ...x, startDate: e.target.value }))} />
            </Field>
            <Field label="Ende" hint="leer = läuft noch" span="md">
                <input type="date" className="input" value={state.endDate} onChange={(e) => setState((x) => ({ ...x, endDate: e.target.value }))} />
            </Field>
            <Field label="Sortierung" span="sm">
                <input type="number" className="input" value={state.sortOrder} onChange={(e) => setState((x) => ({ ...x, sortOrder: e.target.value }))} />
            </Field>
            <Field label="Skills" hint="mit Komma trennen" span="full">
                <input className="input" value={state.skillsText} onChange={(e) => setState((x) => ({ ...x, skillsText: e.target.value }))} placeholder="React, Python, Docker" />
            </Field>
            <Field label="Beschreibung" span="full">
                <textarea className="input cv-textarea" value={state.description} onChange={(e) => setState((x) => ({ ...x, description: e.target.value }))} />
            </Field>
        </div>
    );

    const renderProjectRow = (p) => {
        const open = editId === p.id;
        const period = (p.startDate ? formatDate(p.startDate) : "?") + " – " + (p.endDate ? formatDate(p.endDate) : "läuft noch");
        const skillList = Array.isArray(p.skills) ? p.skills.join(", ") : "";
        return (
            <li key={p.id} className={`cv-entry${open ? " cv-entry--open" : ""}`}>
                <div className="cv-entry-head">
                    <button type="button" className="cv-entry-toggle" aria-expanded={open} onClick={() => toggleProjectEdit(p)}>
                        <span className="cv-entry-chevron" aria-hidden="true">{open ? "▼" : "▶"}</span>
                        <span className="cv-entry-title">{p.title}</span>
                        {p.industry && <span className="cv-chip">{p.industry}</span>}
                        {skillList && <span className="cv-entry-detail">{skillList}</span>}
                        <span className="cv-entry-meta">{period}</span>
                    </button>
                    <div className="cv-entry-actions">
                        <button type="button" className="cv-iconbtn cv-iconbtn--danger"
                            onClick={() => handleDeleteProject(p.id)} aria-label={`${p.title} löschen`} title="Löschen">✕</button>
                    </div>
                </div>
                {open && (
                    <div className="cv-entry-body">
                        {renderProjectFields(editProject, setEditProject)}
                        <div className="cv-entry-buttons">
                            <button type="button" className="button" onClick={saveEdit}>Änderungen speichern</button>
                            <button type="button" className="button button-normal" onClick={cancelEdit}>Abbrechen</button>
                        </div>
                    </div>
                )}
            </li>
        );
    };

    if (role !== "admin") {
        return (
            <div className="employee-profile-container">
                <h2 className="cv-page-title">Mitarbeiter-CV</h2>
                <p className="cv-placeholder">Diese Seite ist nur für Administratoren.</p>
            </div>
        );
    }

    const selectedUser = users.find((u) => u.id === selectedUserId);

    return (
        <div className="employee-profile-container">
            <div className="cv-page-header">
                <div>
                    <h2 className="cv-page-title">Mitarbeiter-CV</h2>
                    <p className="cv-page-subtitle">
                        {selectedUser
                            ? `Profil von ${selectedUser.name} pflegen und als PDF ausgeben`
                            : "Profildaten pflegen und als PDF ausgeben"}
                    </p>
                </div>
                <label className="cv-user-picker">
                    <span className="cv-field-label">Mitarbeiter</span>
                    <select className="input cv-user-select" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.username})</option>)}
                    </select>
                </label>
            </div>

            {/* One list for every Branche field on the page — a datalist is addressed by id. */}
            <datalist id="cv-industries">
                {INDUSTRY_SUGGESTIONS.map((name) => <option key={name} value={name} />)}
            </datalist>

            <div className="cv-tabs">
                {[["profile", "Profil & CV-Daten", entryTotal], ["projects", "Projekte", projects.length], ["variants", "Varianten", variants.length], ["attachments", "Anhänge", attachments.length], ["generate", "CV erstellen", cvFiles.length]].map(([key, label, count]) => (
                    <button key={key} type="button" className={`cv-tab ${activeTab === key ? "cv-tab-active" : ""}`} onClick={() => setActiveTab(key)}>
                        {label}
                        <span className="cv-count">{count}</span>
                    </button>
                ))}
            </div>

            {!selectedUserId && <p className="cv-placeholder">Bitte zuerst einen Mitarbeiter auswählen.</p>}

            {activeTab === "profile" && selectedUserId && (!profileLoaded ? (
                <p className="cv-placeholder">Profil wird geladen …</p>
            ) : (
                <div>
                    {/* Sticky save bar — clears the sticky nav via --cv-sticky-top */}
                    <div className="cv-actionbar">
                        <span className="cv-actionbar-status">
                            <span className={`cv-dot ${dirty ? "cv-dot--dirty" : "cv-dot--clean"}`} aria-hidden="true" />
                            {dirty
                                ? "Nicht gespeicherte Änderungen"
                                : profileExists
                                    ? "Alles gespeichert"
                                    : "Noch kein Profil angelegt – speichern legt es an."}
                        </span>
                        {incompleteTotal > 0 && (
                            <span className="cv-chip cv-chip--warn" title="Einträgen fehlen Angaben, die das PDF braucht">
                                {incompleteTotal} unvollständig
                            </span>
                        )}
                        {saveMsg && <span className="cv-chip cv-chip--ok">{saveMsg}</span>}
                        <button type="button" className="button" onClick={handleSaveProfile} disabled={saving}>
                            {saving ? "Speichert …" : "Profil speichern"}
                        </button>
                    </div>

                    <div className="cv-editor">
                        <nav className="cv-rail" aria-label="Abschnitte">
                            <button type="button" className="cv-rail-link" onClick={() => jumpToSection("basics")}>
                                <span className="cv-rail-label">Stammdaten</span>
                            </button>
                            {sectionStats.map(({ type, count, incomplete }) => (
                                <button key={type} type="button" className="cv-rail-link" onClick={() => jumpToSection(type)}>
                                    <span className="cv-rail-label">{SECTION_META[type].label}</span>
                                    {incomplete > 0 && <span className="cv-chip cv-chip--warn" title={`${incomplete} unvollständig`}>!</span>}
                                    <span className="cv-count">{count}</span>
                                </button>
                            ))}
                            {carriedKeys.length > 0 && (
                                <p className="cv-rail-note">
                                    Zusätzlich im Profil gespeichert: {carriedKeys.join(", ")}. Diese Daten sind hier nicht
                                    bearbeitbar und bleiben beim Speichern unverändert erhalten.
                                </p>
                            )}
                        </nav>

                        <div className="cv-editor-main">
                            <section id="cv-sec-basics" className="cv-section">
                                <div className="cv-section-head">
                                    <h3 className="cv-section-title">Stammdaten</h3>
                                </div>
                                <div className="cv-fields">
                                    <Field label="Berufsbezeichnung" span="xl">
                                        <input className="input" value={profile.title} onChange={(e) => setProfile((p) => ({ ...p, title: e.target.value }))} placeholder="z.B. Senior Software Engineer" />
                                    </Field>
                                    <div className="cv-field cv-field--md">
                                        <span className="cv-field-label">
                                            Foto
                                            <span className="cv-field-hint">optional, PNG oder JPEG</span>
                                        </span>
                                        <div className="cv-photo-row">
                                            {photoUrl
                                                ? <img className="cv-photo" src={photoUrl} alt="Profilfoto" />
                                                : <span className="cv-photo cv-photo--empty" aria-hidden="true">Kein Foto</span>}
                                            <div className="cv-photo-actions">
                                                <label className="button button-normal btn-sm cv-filebtn">
                                                    {hasPhoto ? "Ersetzen" : "Hochladen"}
                                                    <input type="file" accept="image/png,image/jpeg" disabled={photoBusy}
                                                           onChange={(e) => { handleUploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                                                </label>
                                                {hasPhoto && (
                                                    <button type="button" className="button button-normal btn-sm"
                                                            onClick={handleDeletePhoto} disabled={photoBusy}>Entfernen</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Field label="Geburtsdatum" span="md">
                                        <input type="date" className="input" value={profile.birthDate} onChange={(e) => setProfile((p) => ({ ...p, birthDate: e.target.value }))} />
                                    </Field>
                                    <Field label="Kurzprofil" hint="3–5 Sätze, steht oben im PDF" span="full">
                                        <textarea className="input cv-textarea--lg" value={profile.summary} onChange={(e) => setProfile((p) => ({ ...p, summary: e.target.value }))} placeholder="Erfahrener Entwickler mit Schwerpunkt …" />
                                    </Field>
                                    <div className="cv-field cv-field--full">
                                        <label className="cv-field-label" htmlFor="cv-skill-input">
                                            Skills
                                            <span className="cv-count">{skills.length}</span>
                                        </label>
                                        <div className="cv-skill-input">
                                            <input id="cv-skill-input" className="input" value={newSkill}
                                                onChange={(e) => setNewSkill(e.target.value)} onKeyDown={onSkillKeyDown}
                                                placeholder="Skill eingeben und Enter drücken – mehrere mit Komma trennen" />
                                            <button type="button" className="button button-normal" onClick={() => addSkill(newSkill)}>
                                                Hinzufügen
                                            </button>
                                        </div>
                                        <div className="cv-skill-tags">
                                            {skills.map((s) => (
                                                <span key={s} className="cv-skill-tag">
                                                    {s}
                                                    <button type="button" className="cv-skill-remove" onClick={() => removeSkill(s)} aria-label={`Skill ${s} entfernen`}>×</button>
                                                </span>
                                            ))}
                                            {skills.length === 0 && <span className="text-muted">Noch keine Skills – der erste landet direkt im PDF-Kopf.</span>}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {SECTION_ORDER.map(renderSection)}
                        </div>
                    </div>
                </div>
            ))}

            {activeTab === "projects" && selectedUserId && (
                <div className="cv-section">
                    <div className="cv-section-head">
                        <h3 className="cv-section-title">Projekte</h3>
                        <span className="cv-count">{projects.length}</span>
                        <button type="button" className="button cv-section-add" onClick={() => setShowNewProject((v) => !v)}>
                            {showNewProject ? "Eingabe schließen" : "Projekt hinzufügen"}
                        </button>
                    </div>
                    <p className="cv-section-hint">
                        Jede Änderung hier wird sofort gespeichert. Die Projekte erscheinen im PDF unter „Projekte“.
                    </p>

                    {showNewProject && (
                        <div className="cv-draft">
                            <h4 className="cv-draft-title">Neues Projekt</h4>
                            {renderProjectFields(newProject, setNewProject)}
                            <div className="cv-entry-buttons">
                                <button type="button" className="button" onClick={handleCreateProject}>Projekt anlegen</button>
                                <button type="button" className="button button-normal" onClick={() => setShowNewProject(false)}>Abbrechen</button>
                            </div>
                        </div>
                    )}

                    {projects.length === 0 ? (
                        <p className="cv-empty">
                            Noch keine Projekte erfasst.{" "}
                            <button type="button" className="cv-linkbtn" onClick={() => setShowNewProject(true)}>
                                Erstes Projekt anlegen
                            </button>
                        </p>
                    ) : (
                        <ol className="cv-entries">
                            {projects.map(renderProjectRow)}
                        </ol>
                    )}
                </div>
            )}

            {activeTab === "variants" && selectedUserId && (
                <div className="cv-editor-main">
                    <section className="cv-section">
                        <div className="cv-section-head">
                            <h3 className="cv-section-title">Varianten</h3>
                            {!variantsLoading && <span className="cv-count">{variants.length}</span>}
                            <button type="button" className="button button-normal btn-sm cv-section-add"
                                    onClick={() => openVariant(null)} disabled={!!variantForm}>
                                Variante hinzufügen
                            </button>
                        </div>
                        <p className="cv-section-hint">
                            Eine Variante ist eine <strong>Sicht</strong> auf dieses Profil, keine Kopie: sie kann
                            Titel und Kurzprofil überschreiben und aussuchen, welche Skills und Projekte im PDF
                            erscheinen. Berufserfahrung und Ausbildung sind immer vollständig dabei. Korrigierst du
                            ein Projekt, ist es in allen Varianten korrigiert.
                        </p>

                        {variantsLoading ? (
                            <p className="cv-empty">Varianten werden geladen …</p>
                        ) : variants.length === 0 ? (
                            <p className="cv-empty">
                                Noch keine Variante. Ohne Variante erzeugt „CV erstellen" das vollständige Profil –
                                lege eine an, um z.B. eine Fassung mit TwinCAT-Fokus zu erhalten.
                            </p>
                        ) : (
                            <div className="cv-entries">
                                {variants.map((variant) => {
                                    const scope = variantScope(variant, skills, projects);
                                    return (
                                        <div key={variant.id} className="cv-entry">
                                            <div className="cv-entry-head">
                                                <span className="cv-entry-title">{variant.name}</span>
                                                <span className="cv-entry-meta">{variantScopeLabel(scope)}</span>
                                                {scope.titleOverridden && <span className="cv-chip">eigener Titel</span>}
                                                {scope.summaryOverridden && <span className="cv-chip">eigenes Kurzprofil</span>}
                                                <span className="cv-entry-actions">
                                                    <button type="button" className="button button-normal btn-sm"
                                                            onClick={() => openVariant(variant)} disabled={!!variantForm}>
                                                        Bearbeiten
                                                    </button>
                                                    <button type="button" className="button button-danger btn-sm"
                                                            onClick={() => handleDeleteVariant(variant)}>
                                                        Löschen
                                                    </button>
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {variantForm && (
                        <section className="cv-section">
                            <div className="cv-section-head">
                                <h3 className="cv-section-title">
                                    {variantForm.id ? `Variante bearbeiten` : "Neue Variante"}
                                </h3>
                                {editorScope && <span className="cv-entry-meta">{variantScopeLabel(editorScope)}</span>}
                                {variantDirty && <span className="cv-chip cv-chip--warn">Nicht gespeichert</span>}
                            </div>

                            {(variantForm.droppedSkills > 0 || variantForm.droppedProjects > 0) && (
                                <p className="cv-section-hint">
                                    Aus dieser Variante wurden Einträge entfernt, die es im Profil nicht mehr gibt
                                    {variantForm.droppedSkills > 0 && `: ${variantForm.droppedSkills} Skill(s)`}
                                    {variantForm.droppedProjects > 0 && `${variantForm.droppedSkills > 0 ? "," : ":"} ${variantForm.droppedProjects} Projekt(e)`}
                                    . Beim Speichern verschwinden sie endgültig.
                                </p>
                            )}

                            <div className="cv-fields">
                                <Field label="Name der Variante" span="lg">
                                    <input className="input" value={variantForm.name}
                                           onChange={(e) => setVariantField("name", e.target.value)}
                                           placeholder="z.B. TwinCAT-Fokus" />
                                </Field>
                                <CheckField label="Eigener Titel" span="md" checked={variantForm.titleOverride}
                                            onChange={(e) => setVariantField("titleOverride", e.target.checked)} />
                                <Field label="Titel" hint={variantForm.titleOverride ? "" : "wird vom Profil übernommen"} span="xl">
                                    <input className="input" value={variantForm.titleOverride ? variantForm.title : (profile.title || "")}
                                           disabled={!variantForm.titleOverride}
                                           onChange={(e) => setVariantField("title", e.target.value)}
                                           placeholder="z.B. SPS-Entwickler TwinCAT" />
                                </Field>
                                <CheckField label="Eigenes Kurzprofil" span="md" checked={variantForm.summaryOverride}
                                            onChange={(e) => setVariantField("summaryOverride", e.target.checked)} />
                                <Field label="Kurzprofil" hint={variantForm.summaryOverride ? "" : "wird vom Profil übernommen"} span="full">
                                    <textarea className="input cv-textarea"
                                              value={variantForm.summaryOverride ? variantForm.summary : (profile.summary || "")}
                                              disabled={!variantForm.summaryOverride}
                                              onChange={(e) => setVariantField("summary", e.target.value)} />
                                </Field>
                            </div>

                            {[
                                {
                                    key: "skills", label: "Skills", modeField: "skillsMode", pickField: "skills",
                                    all: skills, idOf: (x) => x, labelOf: (x) => x,
                                    allLabel: `Alle ${skills.length} Skills aus dem Profil`,
                                },
                                {
                                    key: "projects", label: "Projekte", modeField: "projectsMode", pickField: "projectIds",
                                    all: projects, idOf: (p) => String(p.id), labelOf: (p) => p.title || "Ohne Titel",
                                    allLabel: `Alle ${projects.length} Projekte aus dem Profil`,
                                },
                            ].map((group) => (
                                <div key={group.key} className="cv-variant-group">
                                    <span className="cv-field-label">{group.label}</span>
                                    <div className="cv-variant-modes" role="radiogroup" aria-label={group.label}>
                                        {[[VARIANT_ALL, group.allLabel], [VARIANT_PICK, "Auswählen"]].map(([mode, text]) => (
                                            <label key={mode} className="cv-variant-mode">
                                                <input type="radio" name={`${group.key}-mode`} value={mode}
                                                       checked={variantForm[group.modeField] === mode}
                                                       onChange={() => setVariantField(group.modeField, mode)} />
                                                <span>{text}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {variantForm[group.modeField] === VARIANT_PICK && (
                                        group.all.length === 0 ? (
                                            <p className="cv-empty">Im Profil ist nichts hinterlegt.</p>
                                        ) : (
                                            <div className="cv-picklist">
                                                {group.all.map((item) => {
                                                    const id = group.idOf(item);
                                                    return (
                                                        <label key={id} className="cv-picklist-item">
                                                            <input type="checkbox"
                                                                   checked={(variantForm[group.pickField] || []).includes(id)}
                                                                   onChange={() => toggleVariantPick(group.pickField, id)} />
                                                            <span>{group.labelOf(item)}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )
                                    )}
                                </div>
                            ))}

                            <div className="cv-entry-buttons">
                                <button type="button" className="button" onClick={handleSaveVariant} disabled={variantSaving}>
                                    {variantSaving ? "Speichert …" : variantForm.id ? "Änderungen speichern" : "Variante anlegen"}
                                </button>
                                <button type="button" className="button button-normal" onClick={closeVariant} disabled={variantSaving}>
                                    Abbrechen
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            )}

            {activeTab === "attachments" && selectedUserId && (
                <div className="cv-editor-main">
                    <section className="cv-section">
                        <div className="cv-section-head">
                            <h3 className="cv-section-title">Anhänge</h3>
                            <span className="cv-count">{attachments.length}</span>
                        </div>
                        <p className="cv-section-hint">
                            Dokumente, die <strong>hinter</strong> das CV gehängt werden können – Empfehlungen,
                            Referenzen, Zeugnisse. Beim Erstellen wird je Anhang entschieden, ob er mitkommt.
                            PDF, PNG oder JPEG, bis 10 MB; das CV nennt sie am Ende in dieser Reihenfolge.
                        </p>
                        <div className="cv-fields">
                            <Field label="Titel" hint="steht so im CV; leer = Dateiname" span="xl">
                                <input className="input" value={attachmentTitle}
                                       onChange={(e) => setAttachmentTitle(e.target.value)}
                                       placeholder="z.B. Empfehlungsschreiben Müller GmbH" />
                            </Field>
                            <div className="cv-field cv-field--md">
                                <span className="cv-field-label" aria-hidden="true">&nbsp;</span>
                                <label className="button cv-filebtn">
                                    Datei wählen und hochladen
                                    <input type="file" accept="application/pdf,image/png,image/jpeg"
                                           disabled={attachmentBusy === "upload"}
                                           onChange={(e) => { handleUploadAttachment(e.target.files?.[0]); e.target.value = ""; }} />
                                </label>
                            </div>
                        </div>

                        {attachmentsLoading ? (
                            <p className="cv-empty">Anhänge werden geladen …</p>
                        ) : attachments.length === 0 ? (
                            <p className="cv-empty">Noch keine Anhänge hinterlegt.</p>
                        ) : (
                            <ol className="cv-entries">
                                {attachments.map((a, idx) => (
                                    <li key={a.id} className="cv-entry">
                                        <div className="cv-entry-head">
                                            <span className="cv-entry-toggle cv-entry-toggle--static">
                                                <span className="cv-entry-title">{a.title}</span>
                                                <span className="cv-entry-detail">{a.fileName}</span>
                                                <span className="cv-entry-meta">
                                                    {typeof a.fileSize === "number" ? `${Math.round(a.fileSize / 1024)} KB` : ""}
                                                </span>
                                            </span>
                                            <div className="cv-entry-actions">
                                                <button type="button" className="cv-iconbtn" disabled={idx === 0 || !!attachmentBusy}
                                                        onClick={() => handleMoveAttachment(idx, -1)}
                                                        aria-label={`${a.title} nach oben`} title="Nach oben">↑</button>
                                                <button type="button" className="cv-iconbtn" disabled={idx === attachments.length - 1 || !!attachmentBusy}
                                                        onClick={() => handleMoveAttachment(idx, 1)}
                                                        aria-label={`${a.title} nach unten`} title="Nach unten">↓</button>
                                                <button type="button" className="cv-iconbtn"
                                                        onClick={() => handlePreviewAttachment(a)}
                                                        aria-label={`${a.title} ansehen`} title="Ansehen">👁</button>
                                                <button type="button" className="cv-iconbtn" disabled={!!attachmentBusy}
                                                        onClick={() => handleRenameAttachment(a)}
                                                        aria-label={`${a.title} umbenennen`} title="Umbenennen">✎</button>
                                                <button type="button" className="cv-iconbtn cv-iconbtn--danger" disabled={!!attachmentBusy}
                                                        onClick={() => handleDeleteAttachment(a)}
                                                        aria-label={`${a.title} löschen`} title="Löschen">✕</button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </section>
                </div>
            )}

            {activeTab === "generate" && selectedUserId && (
                <div className="cv-editor-main">
                    <section className="cv-section">
                        <div className="cv-section-head">
                            <h3 className="cv-section-title">CV als PDF erstellen</h3>
                            {entryTotal === 0 && projects.length === 0 && (
                                <span className="cv-chip cv-chip--warn">Profil ist noch leer</span>
                            )}
                        </div>
                        <p className="cv-section-hint">
                            Das PDF entsteht aus den <strong>gespeicherten</strong> Profildaten, wird heruntergeladen und
                            zusätzlich in der Dateiliste unten abgelegt.
                        </p>
                        <div className="cv-fields">
                            <Field label="Variante" hint={variants.length === 0 ? "keine angelegt" : ""} span="lg">
                                <select className="input" value={cvVariantId}
                                        onChange={(e) => setCvVariantId(e.target.value)}>
                                    <option value="">Ganzes Profil</option>
                                    {variants.map((v) => (
                                        <option key={v.id} value={String(v.id)}>{v.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Titel im Dateinamen" span="lg">
                                <input className="input" value={cvTitle} onChange={(e) => setCvTitle(e.target.value)} placeholder={profile.title || "z.B. Senior_Engineer_CV"} />
                            </Field>
                            <Field label="Kommentar" hint="optional" span="lg">
                                <input className="input" value={cvComment} onChange={(e) => setCvComment(e.target.value)} placeholder="z.B. Version für Kunde X" />
                            </Field>
                        </div>
                        <p className="cv-section-hint">
                            {generateScope
                                ? `„${selectedVariant.name}" enthält ${variantScopeLabel(generateScope)}.`
                                : "Ohne Variante enthält das PDF das vollständige Profil."}
                        </p>

                        <div className="cv-pick">
                            <span className="cv-field-label">Foto</span>
                            {hasPhoto ? (
                                <label className="cv-check">
                                    <input type="checkbox" checked={cvIncludePhoto}
                                           onChange={(e) => setCvIncludePhoto(e.target.checked)} />
                                    <span>Profilfoto in den Kopf des CV aufnehmen</span>
                                </label>
                            ) : (
                                <p className="cv-empty">
                                    Kein Foto hinterlegt – unter <strong>Profil &amp; CV-Daten</strong> hochladen.
                                </p>
                            )}
                        </div>

                        <div className="cv-pick">
                            <span className="cv-field-label">
                                Anhänge
                                <span className="cv-field-hint">
                                    {attachments.length === 0
                                        ? "keine hinterlegt"
                                        : `${cvAttachmentIds.length} von ${attachments.length} ausgewählt`}
                                </span>
                            </span>
                            {attachments.length === 0 ? (
                                <p className="cv-empty">
                                    Unter <strong>Anhänge</strong> hinzufügen – z.B. Empfehlungen oder Referenzen.
                                </p>
                            ) : (
                                <ul className="cv-checklist">
                                    {attachments.map((a) => (
                                        <li key={a.id}>
                                            <label className="cv-check">
                                                <input type="checkbox" checked={cvAttachmentIds.includes(a.id)}
                                                       onChange={() => toggleCvAttachment(a.id)} />
                                                <span>{a.title}</span>
                                                <span className="cv-entry-detail">{a.fileName}</span>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="cv-entry-buttons">
                            <button type="button" className="button" onClick={handleGenerateCv} disabled={!profileLoaded || cvGenerating}>
                                {cvGenerating ? "PDF wird erstellt …" : "PDF erstellen und herunterladen"}
                            </button>
                        </div>
                    </section>

                    <section className="cv-section">
                        <div className="cv-section-head">
                            <h3 className="cv-section-title">Gespeicherte Dateien</h3>
                            {!cvFilesLoading && <span className="cv-count">{cvFiles.length}</span>}
                            <button type="button" className="button button-normal btn-sm cv-section-add" onClick={() => loadCvFiles(selectedUserId)} disabled={cvFilesLoading}>
                                {cvFilesLoading ? "Lädt …" : "Aktualisieren"}
                            </button>
                        </div>
                        {cvFilesLoading ? (
                            <p className="cv-empty">Dateien werden geladen …</p>
                        ) : cvFiles.length === 0 ? (
                            <p className="cv-empty">Noch kein PDF erstellt – oben anfangen.</p>
                        ) : (
                            <div className="cv-table-wrap">
                                <table className="table">
                                    <thead><tr><th>Datei</th><th>Variante</th><th>Größe</th><th>Erstellt</th><th>Aktionen</th></tr></thead>
                                    <tbody>
                                        {cvFiles.map((f) => (
                                            <tr key={f.key}>
                                                <td>{f.fileName || f.key}</td>
                                                {/* Recovered from the file name, not stored on the file — an
                                                    unrecognised name shows a dash rather than a guess. */}
                                                <td>{variantFileMatch(f.fileName || f.key, variants)?.name || "—"}</td>
                                                <td>{typeof f.size === "number" ? `${Math.round(f.size / 1024)} KB` : "-"}</td>
                                                <td>{f.lastModified ? new Date(f.lastModified).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                                                <td>
                                                    <div className="cv-file-actions">
                                                        <a href={f.url} target="_blank" rel="noreferrer" className="cv-file-link">Öffnen</a>
                                                        <button type="button" className="cv-iconbtn cv-iconbtn--danger"
                                                                onClick={() => handleDeleteCvFile(f)} disabled={cvFileDeleting === f.key}
                                                                aria-label={`${f.fileName || f.key} löschen`} title="Löschen">✕</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
